import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Mail } from "lucide-react";
import OtpInput from "react-otp-input";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  requestEmailVerifyOtp,
  resendEmailVerifyOtp,
  verifyEmailOtp,
} from "../../store/Authentication/AuthSlice";
import { fetchData } from "../../store/Authentication/RolesSlice";
import { encryptEmailOtp } from "../../utils/clientCrypto";
import { formatCountdown } from "../../utils/countdown";
// The code section reuses the login-with-OTP styles (scoped to .authCard).
import "../../forms/Auth/AuthCard.css";

// No leading/trailing/double dots in the local part; domain labels are
// letters, digits and hyphens, ending in a 2+ letter TLD.
const EMAIL_REGEX = /^[^\s@.]+(\.[^\s@.]+)*@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;
const OTP_LENGTH = 6;
const RESEND_SECONDS = 120;
// Fallback only — the server sends the real lifetime (expiresInSeconds).
const OTP_TTL_SECONDS = 120;
const TOAST_ID = "verify-email-toast";

const notify = (type, message) => toast[type](message, { containerId: TOAST_ID });

/**
 * Shown when an unverified customer has used up their free rate searches
 * (see utils/queryLimit.js). Sends a code to the email, then verifies it
 * with the same code boxes and resend cooldown as LoginWithOtp.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose - cancel / dismiss
 * @param {Function} [props.onVerified] - called instead of onClose once the
 *   email is verified and /user-details has been refreshed
 * @param {string} [props.defaultEmail] - prefilled from /user-details
 * @param {string} [props.userId]
 */
const VerifyEmailModal = ({ open, onClose, onVerified, defaultEmail, userId }) => {
  const dispatch = useDispatch();
  const dialogRef = useRef(null);

  // defaultEmail can be null (not just undefined) from /user-details.
  const [email, setEmail] = useState(defaultEmail || "");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [resendLocked, setResendLocked] = useState(false);
  // Seconds the current code stays valid; 0 once sent means expired.
  const [expiresSeconds, setExpiresSeconds] = useState(0);
  // Keeps the toast container mounted after close so the success toast shows.
  const [hasOpened, setHasOpened] = useState(false);

  const trimmedEmail = (email || "").trim();
  const isEmailValid = EMAIL_REGEX.test(trimmedEmail);
  const busy = sending || verifying;
  const otpExpired = otpSent && expiresSeconds <= 0;

  const resetOtpSection = () => {
    setOtpSent(false);
    setOtp("");
    setResendSeconds(0);
    setResendLocked(false);
    setExpiresSeconds(0);
  };

  // Re-prefill and reset every time the modal opens.
  useEffect(() => {
    if (!open) return;
    setHasOpened(true);
    setEmail(defaultEmail || "");
    resetOtpSection();
  }, [open, defaultEmail]);

  // Move focus into the dialog on open and hand it back to the page on close.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement;
    const firstField = dialogRef.current?.querySelector("input:not([disabled])");
    (firstField || dialogRef.current)?.focus();
    // Pull focus back if it lands on the page behind the overlay.
    const keepFocusInside = (e) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target)) dialogRef.current.focus();
    };
    document.addEventListener("focusin", keepFocusInside);
    return () => {
      document.removeEventListener("focusin", keepFocusInside);
      previouslyFocused?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = setTimeout(() => setResendSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendSeconds]);

  useEffect(() => {
    if (expiresSeconds <= 0) return;
    const timer = setTimeout(() => setExpiresSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [expiresSeconds]);

  // Waits for the refreshed profile so the caller sees the verified email.
  const finishVerified = () =>
    dispatch(fetchData())
      .catch(() => {})
      .then(() => (onVerified || onClose)());

  const sendCode = (isResend) => {
    setSending(true);
    const thunk = isResend ? resendEmailVerifyOtp : requestEmailVerifyOtp;
    return dispatch(thunk({ email: trimmedEmail, userId }))
      .then((res) => {
        const status = res?.payload?.status;
        const body = res?.payload?.data;
        const message = body?.message;

        if (status === 200) {
          setOtpSent(true);
          setOtp("");
          setResendSeconds(Number(body?.data?.resendAfterSeconds) || RESEND_SECONDS);
          setExpiresSeconds(Number(body?.data?.expiresInSeconds) || OTP_TTL_SECONDS);
          notify("success", message || "A verification code has been sent to your email.");
          return;
        }
        // The earlier code expired or was never sent — request a fresh one.
        if (isResend && body?.code === "no_active_request") return sendCode(false);
        // A code is already out for this email, so let the user enter it.
        if (body?.code === "resend_cooldown") {
          setOtpSent(true);
          setResendSeconds(Number(body?.retryAfterSeconds) || RESEND_SECONDS);
          // The code lives as long as the cooldown, so it has this long left.
          setExpiresSeconds(Number(body?.retryAfterSeconds) || OTP_TTL_SECONDS);
          notify("info", message || "Please wait before requesting another code.");
          return;
        }
        if (body?.code === "resend_limit") setResendLocked(true);
        if (status === 409 && /already verified/i.test(message || "")) {
          notify("success", message);
          return finishVerified();
        }
        // Registered with another account: the user needs a different email.
        if (status === 409) resetOtpSection();
        notify("error", message || "We couldn't send the verification code. Please try again.");
      })
      .catch(() => notify("error", "Something went wrong. Please try again."))
      .finally(() => setSending(false));
  };

  const handleSendOtp = () => {
    if (!isEmailValid || busy) return; // prevents duplicate submissions
    if (!userId) {
      notify("error", "We couldn't load your account details. Please refresh and try again.");
      return;
    }
    sendCode(false);
  };

  const handleVerify = async () => {
    if (otp.length !== OTP_LENGTH || busy) return; // prevents duplicate submissions
    if (otpExpired) {
      notify("error", "This code has expired. Please request a new one.");
      return;
    }
    // Set before encrypting so the controls are locked for that await too.
    setVerifying(true);

    // No plaintext fallback, same rule as the signup OTP (utils/clientCrypto.js).
    let encryptedOtp;
    try {
      encryptedOtp = await encryptEmailOtp(otp, trimmedEmail);
    } catch (err) {
      // console.error("OTP encryption failed");
      notify("error", "Your browser couldn't secure the code. Please try another browser.");
      setVerifying(false);
      return;
    }

    dispatch(verifyEmailOtp({ email: trimmedEmail, userId, otp: encryptedOtp }))
      .then((res) => {
        const status = res?.payload?.status;
        const body = res?.payload?.data;

        if (status === 200) {
          notify("success", body?.message || "Email verified successfully!");
          // Password mail is disabled (OTP-only login), so no "Forgot password" hint.
          // if (body?.data?.passwordGenerated && !body?.data?.passwordSent) {
          //   notify("warning", "We couldn't email your login details. Use \"Forgot password\" to set a password.");
          // }
          return finishVerified();
        }
        if (status === 409) resetOtpSection();
        setOtp("");
        notify("error", body?.message || "The code you entered is invalid or has expired.");
      })
      .catch(() => notify("error", "Something went wrong. Please try again."))
      .finally(() => setVerifying(false));
  };

  // Rendered from the same spot whether or not the modal is open — moving it
  // between two return paths remounted it on close and dropped the
  // success toast fired just before onClose().
  const toastContainer = hasOpened && (
    <ToastContainer containerId={TOAST_ID} position="top-center" autoClose={5000} style={{ zIndex: 10000 }} />
  );

  if (!open) return <>{null}{toastContainer}</>;

  const primaryDisabled = otpSent ? otp.length !== OTP_LENGTH || busy || otpExpired : !isEmailValid || busy;

  // Escape cancels; Tab / Shift+Tab wrap around inside the dialog.
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      if (!busy) onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const focusable = Array.from(
      dialogRef.current?.querySelectorAll("button:not([disabled]), input:not([disabled])") || []
    );
    if (!focusable.length) {
      e.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0,0,0,0.45)",
          zIndex: 9999,
        }}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="verify-email-title"
          aria-describedby="verify-email-description"
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          style={{
            background: "#fff",
            borderRadius: 16,
            outline: "none",
            boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
            padding: "28px 32px 24px",
            maxWidth: 460,
            width: "90%",
            position: "relative",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            style={{
              position: "absolute",
              top: 14,
              right: 16,
              background: "none",
              border: "none",
              cursor: busy ? "not-allowed" : "pointer",
              color: "#78716c",
              fontSize: 20,
              lineHeight: 1,
              padding: 4,
            }}
          >
            ×
          </button>
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <div
              style={{
                flexShrink: 0,
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "#ffaf00",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Mail size={18} color="#fff" aria-hidden="true" />
            </div>
            <p
              id="verify-email-title"
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 700,
                color: "#292524",
              }}
            >
              Verify to continue searching
            </p>
          </div>
          <p
            id="verify-email-description"
            style={{
              margin: "0 0 20px",
              fontSize: 13.5,
              color: "#57534e",
              lineHeight: 1.6,
            }}
          >
            {otpSent
              ? "Enter the 6-digit code we sent to your email."
              : "You've used your free searches. Verify your email to keep searching rates."}
          </p>

          {!otpSent ? (
            <>
              <label
                htmlFor="verify-email-input"
                style={{
                  display: "block",
                  marginBottom: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                  color: "#78716c",
                }}
              >
                Email <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                id="verify-email-input"
                type="email"
                value={email}
                maxLength={255}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={busy}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: `1px solid ${email && !isEmailValid ? "#dc2626" : "#e7e5e4"}`,
                  fontSize: 14,
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              {email && !isEmailValid && (
                <p style={{ margin: "6px 0 0", fontSize: 12, color: "#dc2626" }}>
                  Enter a valid email address.
                </p>
              )}
              {defaultEmail && (
                <p style={{ margin: "6px 0 0", fontSize: 12, color: "#78716c" }}>
                  This is the email you signed up with. Continue with it, or enter a different one.
                </p>
              )}
            </>
          ) : (
            <div className="authCard" style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              <div className="otp-note" style={{ marginTop: 0 }}>Code sent to {trimmedEmail}</div>
              <div>
                <label className="field-label">Enter 6-digit code</label>
                <div style={{ marginTop: "0.45rem" }}>
                  <OtpInput
                    value={otp}
                    onChange={setOtp}
                    numInputs={OTP_LENGTH}
                    shouldAutoFocus
                    inputType="tel"
                    containerStyle="otp-boxes"
                    // style stripped so the .otp-boxes input styles win
                    renderInput={(props) => <input {...props} style={undefined} disabled={verifying} />}
                  />
                </div>
                {/* .otp-note's negative top margin suits the login card, not
                    this modal — it pulled the note into the code boxes. */}
                <div
                  className="otp-note"
                  style={{ marginTop: "0.6rem", lineHeight: 1.4, ...(otpExpired && { color: "#dc2626" }) }}
                >
                  {otpExpired ? "Code expired. Please request a new one." : `Code expires in ${formatCountdown(expiresSeconds)}`}
                </div>
              </div>
              <div className="otp-actions">
                <button type="button" className="btn-change" disabled={busy} onClick={resetOtpSection}>
                  Change email
                </button>
                <button
                  type="button"
                  className="btn-resend"
                  disabled={resendSeconds > 0 || resendLocked || busy}
                  onClick={() => sendCode(true)}
                >
                  {sending ? "Sending..." : resendSeconds > 0 ? `Resend in ${formatCountdown(resendSeconds)}` : "Resend OTP"}
                </button>
              </div>
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              marginTop: 24,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              style={{
                padding: "10px 22px",
                borderRadius: 8,
                border: "1px solid #e7e5e4",
                background: "#fff",
                color: "#292524",
                fontSize: 14,
                fontWeight: 600,
                cursor: busy ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={otpSent ? handleVerify : handleSendOtp}
              disabled={primaryDisabled}
              style={{
                padding: "10px 28px",
                borderRadius: 8,
                border: "none",
                background: "#ffaf00",
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                opacity: primaryDisabled ? 0.6 : 1,
                cursor: primaryDisabled ? "not-allowed" : "pointer",
              }}
            >
              {otpSent
                ? verifying ? "Verifying..." : "Verify Email"
                : sending ? "Sending OTP..." : "Send OTP to Verify"}
            </button>
          </div>
        </div>
      </div>
      {toastContainer}
    </>
  );
};

export default VerifyEmailModal;
